// import Vue from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import { isImpersonateMode, getOrderPaymentLink, getStripeCustomerPortals } from '../mixins/requests';
import { isObjectEmpty, isInImpersonateModeNotification } from '../mixins/utils';
import store from '../store';

// MENUS
import Entry from '@/components/common/Entry';
import Home from '@/views/Home';
import Profile from '@/views/Profile';
import Billets from '@/views/Billets';
import Reservations from '@/views/Reservations';
import ScheduledReservations from '@/views/ScheduledReservations';
import History from '@/views/History';
import AllowedProduct from '@/views/AllowedProduct';

// BILLETS
import TicketList from '@/components/billets/TicketList';
import TicketInfo from '@/components/billets/TicketInfo';

// RESERVATIONS
import BilletsList from '@/components/reservations/BilletsList';
import ScheduledList from '@/components/reservations/ScheduledList';
import ActivitiesList from '@/components/reservations/ActivitiesList';
import SessionActivityList from '@/components/reservations/SessionActivityList';
import SessionsPlanner from '@/components/reservations/SessionsPlanner';

// PROFILE
import MyContacts from '@/components/profile/MyContacts';
// import Documents from '@/components/profile/Documents';
import ContactPreferences from '@/components/profile/ContactPreferences';
import MyProfile from '@/components/profile/MyProfile';
import ContactForm from '@/components/profile/ContactForm';
import EditUser from '@/components/profile/EditUser';
import Insurance from '@/components/profile/Insurance';
import License from '@/components/profile/License';
import OrderDetails from '@/components/history/OrderDetails';
import OrderInvoices from '@/components/history/OrderInvoices';
import OrderReceipts from '@/components/history/OrderReceipts';

const helloURL = process.env.VUE_APP_HELLO_URL;

const routes = [
  {
    name: 'Entry',
    path: '/:site_preroute/espacepersonnel/:site_url?',
    redirect: to => {
      const params = { site_preroute: to.params.site_preroute };

      if (to.params.site_url) params.site_url = to.params.site_url;
      return { name: 'Home', params: { ...params } };
    },
    component: Entry,
    children: [
      {
        name: 'Home',
        path: 'accueil',
        component: Home,
        meta: {
          icon: 'icon-accueil',
          visible: true,
          breadcrumb: false,
          title: 'Accueil'
        }
      },
      {
        name: 'Offers',
        path: 'offers',
        component: AllowedProduct,
        meta: {
          show: ['SHOWOFFERTAB'],
          icon: 'icon-ticket',
          visible: true,
          breadcrumb: false,
          title: 'Mes Offres'
        }
      },
      {
        name: 'Billets',
        path: 'e-billets',
        component: Billets,
        redirect: { name: 'TicketList' },
        meta: {
          icon: 'icon-ticket',
          visible: true,
          breadcrumb: false,
          title: 'Mes E-Billets'
        },
        children: [
          {
            name: 'TicketList',
            path: '',
            component: TicketList,
            meta: {
              icon: 'icon-ticket',
              visible: true,
              breadcrumb: false,
              title: 'Mes E-Billets'
            }
          },
          {
            name: 'TicketInfo',
            path: ':qrcode',
            component: TicketInfo,
            meta: {
              icon: 'icon-ticket',
              visible: true,
              breadcrumb: true,
              title: 'E-Billet'
            }
          }
        ]
      },
      {
        name: 'Reservations',
        path: 'reservations',
        component: Reservations,
        redirect: { name: 'BilletsList' },
        meta: {
          icon: 'icon-date-check-icon',
          visible: true,
          breadcrumb: false,
          title: 'Réserver'
        },
        children: [
          {
            name: 'BilletsList',
            path: '',
            component: BilletsList,
            meta: {
              icon: 'icon-date-check-icon',
              visible: true,
              breadcrumb: false,
              title: 'Réserver'
            },
            children: [
              {
                name: 'ActivitiesList',
                path: ':billet_uuid',
                component: ActivitiesList,
                meta: {
                  visible: false,
                  breadcrumb: true,
                  title: 'Activités',
                  breadcrumbTitle: 'Choisir une activité'
                },
                children: [
                  {
                    name: 'SessionsPlanner',
                    path: 'sessions',
                    component: SessionsPlanner,
                    meta: {
                      visible: false,
                      breadcrumb: true,
                      title: 'Planning',
                      breadcrumbTitle: null
                    },
                    children: [
                      {
                        name: 'SessionActivityList',
                        path: 'activities/:activity_id?',
                        component: SessionActivityList,
                        beforeEnter: async (to, from, next) => {
                          const { session } = store.state;
                          if (session && session.selectedActivityType) {
                            switch (session.selectedActivityType.toUpperCase()) {
                              case 'POSTE':
                                to.meta.title = 'Postes';
                                break;
                              case 'TERRAIN':
                              default:
                                to.meta.title = 'Terrains';
                                break;
                            }
                            const matchedRoute = to.matched.find(m => m.name === to.name);
                            matchedRoute.meta.title = to.meta.title;
                          }
                          next();
                        },
                        meta: {
                          visible: false,
                          breadcrumb: true,
                          title: 'Terrains',
                          breadcrumbTitle: null
                        }
                      }
                    ]
                  }
                ]
              },
              {
                name: 'GroupSessionsPlanner',
                path: ':billet_uuid/group',
                component: SessionsPlanner,
                meta: {
                  visible: false,
                  breadcrumb: true,
                  title: 'Planning',
                  breadcrumbTitle: null
                },
                children: [
                  {
                    name: 'GroupSessionActivityList',
                    path: 'activities/:activity_id?',
                    component: SessionActivityList,
                    beforeEnter: async (to, from, next) => {
                      const { session } = store.state;
                      if (session && session.selectedActivityType) {
                        switch (session.selectedActivityType.toUpperCase()) {
                          case 'POSTE':
                            to.meta.title = 'Postes';
                            break;
                          case 'TERRAIN':
                          default:
                            to.meta.title = 'Terrains';
                            break;
                        }
                        const matchedRoute = to.matched.find(m => m.name === to.name);
                        matchedRoute.meta.title = to.meta.title;
                      }
                      next();
                    },
                    meta: {
                      visible: false,
                      breadcrumb: true,
                      title: 'Terrains',
                      breadcrumbTitle: null
                    }
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        name: 'ScheduledReservations',
        path: 'scheduled-reservations',
        component: ScheduledReservations,
        redirect: { name: 'ScheduledList' },
        meta: {
          icon: 'icon-date-refresh-icon',
          visible: true,
          breadcrumb: false,
          title: 'Mes réservations'
        },
        children: [
          {
            name: 'ScheduledList',
            path: '',
            component: ScheduledList,
            meta: {
              icon: 'icon-date-refresh-icon',
              visible: true,
              breadcrumb: false,
              title: 'Mes réservations'
            }
          }
        ]
      },
      {
        name: 'Mes Tchats',
        path: 'hello-chat',
        beforeEnter: (to, from, next) => {
          window.location.href = `${helloURL}?returnPath=${store.state.siteInfo.site_preroute}/espacepersonnel/${store.state.siteInfo.siteUrl}`;
        },
        meta: {
          icon: 'icon-commenting-o',
          visible: helloURL,
          breadcrumb: false,
          title: 'Mes Tchats'
        }
      },
      {
        name: 'Informations',
        path: 'profile',
        component: Profile,
        meta: {
          icon: 'icon-profile',
          visible: true,
          breadcrumb: false,
          title: 'Mes Informations'
        },
        children: [
          {
            name: 'MyProfile',
            path: 'informations-personnelles',
            component: MyProfile,
            meta: {
              visible: true,
              breadcrumb: true,
              title: 'Informations Personnelles'
            },
            children: [
              {
                name: 'EditMyProfile',
                path: 'editer',
                component: EditUser,
                meta: {
                  visible: true,
                  breadcrumb: true,
                  title: 'Modifier vos Informations Personnelles'
                }
              }
            ]
          },
          {
            name: 'ContactPreferences',
            path: 'preferences-contact',
            component: ContactPreferences,
            meta: {
              visible: true,
              breadcrumb: true,
              title: 'Préférences de Contact'
            }
          },
          // {
          //   name: 'Documents',
          //   path: 'prestations',
          //   component: Documents,
          //   meta: {
          //     visible: true,
          //     breadcrumb: true,
          //     title: 'Documents administratifs'
          //   }
          // },
          {
            name: 'MyContacts',
            path: 'mes-proches',
            component: MyContacts,
            meta: {
              visible: true,
              breadcrumb: true,
              title: 'Mes Proches'
            },
            children: [
              {
                name: 'AddContact',
                path: 'nouveau',
                component: ContactForm,
                meta: {
                  breadcrumb: true,
                  title: 'Ajouter un Proche'
                }
              },
              {
                name: 'EditContact',
                path: ':uuid',
                component: ContactForm,
                meta: {
                  breadcrumb: true,
                  title: 'Détails de Votre Proche'
                }
              }
            ]
          },
          {
            name: 'Commandes',
            path: 'commands',
            component: History,
            meta: {
              icon: 'icon-rewind',
              visible: true,
              breadcrumb: true,
              title: 'Achats et moyens de paiement'
            },
            children: [
              {
                name: 'OrderDetails',
                path: ':uuid',
                component: OrderDetails,
                meta: {
                  breadcrumb: true,
                  title: 'Ma commande'
                },
                children: [
                  {
                    name: 'OrderInvoices',
                    path: 'factures',
                    component: OrderInvoices,
                    meta: {
                      breadcrumb: true,
                      title: 'Mes factures'
                    }
                  },
                  {
                    name: 'OrderReceipts',
                    path: 'recus',
                    component: OrderReceipts,
                    meta: {
                      breadcrumb: true,
                      title: 'Mes reçus de paiement'
                    }
                  },
                  {
                    name: 'PaymentsLink',
                    path: 'mes-paiements',
                    beforeEnter: async (to, from, next) => {
                      try {
                        if (isInImpersonateModeNotification()) {
                          next(false);
                          return;
                        }
                        await getOrderPaymentLink(to.params.uuid, store.state.siteInfo.workspace).catch(() => next(false));
                        next(false);
                      } catch (e) {
                        console.log(e);
                        next(false);
                      }
                    },
                    meta: {
                      visible: true,
                      breadcrumb: false,
                      title: 'Modifier mes coodonnées bancaires'
                    }
                  }
                ]
              }
            ]
          },
          {
            name: 'Mes tchats',
            path: 'hello-chat',
            beforeEnter: (to, from, next) => {
              window.location.href = `${helloURL}?returnPath=${store.state.siteInfo.site_preroute}/espacepersonnel/${store.state.siteInfo.siteUrl}`;
            },
            meta: {
              icon: 'icon-commenting-o',
              visible: helloURL,
              breadcrumb: true,
              title: 'Mes tchats'
            }
          },
          {
            name: 'PaymentsStripeLink',
            path: 'mes-paiements',
            beforeEnter: async (to, from, next) => {
              if (isInImpersonateModeNotification()) {
                next(false);
                return;
              }
              await getStripeCustomerPortals('payments').catch(() => next(false));
              next(false);
            },
            meta: {
              visible: () => store.state.user && store.state.user.stripe_id && store.state.siteInfo.perso_settings && store.state.siteInfo.perso_settings.showStripeLink,
              breadcrumb: false,
              title: 'Mes Moyens De Paiement Stripe'
            }
          },
          {
            name: 'Assurance',
            path: 'insurance',
            component: Insurance,
            meta: {
              show: ['SHOWINSURANCETAB'],
              icon: 'icon-familia2',
              visible: true,
              breadcrumb: true,
              title: 'Mes Assurances'
            }
          },
          {
            name: 'Licence',
            path: 'license',
            component: License,
            meta: {
              show: ['SHOWLICENSETAB'],
              icon: 'icon-bill',
              visible: true,
              breadcrumb: true,
              title: 'Mes Licences'
            }
          }
        ]
      }
    ]
  }
  // {
  //   path: '/:pathMatch(.*)*',
  //   redirect: { name: 'Entry', params: { site_preroute: 'sport-station', site_url: 'grand-reims' } }
  // }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

router.beforeEach(async (to, from, next) => {
  if (store.state.siteInfo && Object.keys(store.state.siteInfo).length && ((store.state.siteInfo.entry_site && !to.params.site_preroute) || (!store.state.siteInfo.entry_site && !to.params.site_url))) {
    return next({
      ...to,
      params: {
        site_preroute: from.params.site_preroute,
        site_url: from.params.site_url,
        ...to.params
      }
    });
  }

  const disabled_features = (store.state.siteInfo.perso_settings && store.state.siteInfo.perso_settings.disabled_features) ? store.state.siteInfo.perso_settings.disabled_features : [];

  const feature = to.fullPath.split('/').pop();
  const path = to.fullPath.replace(`/${feature}`, '');
  const feature_parent = path.split('/').pop();

  if (!isObjectEmpty(store.state.siteInfo) && store.state.impersonateData) {
    const response = await isImpersonateMode();

    if (!response.success || response.data.workspace !== store.state.siteInfo.workspace) {
      store.commit('setImpersonateData', null);
      const url = `/${to.params.site_preroute}${to.params.site_url ? `/${to.params.site_url}` : ''}`;
      window.location.href = url;
      return;
    }
  }

  if (disabled_features.length && disabled_features.includes(feature)) {
    if ([to.params.site_url, 'espacepersonnel'].includes(feature_parent)) {
      return next(path);
    }
    return next({ name: 'Home', params: { site_preroute: to.params.site_preroute, site_url: to.params.site_url } });
  }
  return next();
});

// router.afterEach(async (to, from) => {
//   const response = await isImpersonateMode();

//   if (!response.success) {
//     const cookie = Cookies.get('impersonateMode');
//     store.commit('setImpersonateData', null);
//     if (cookie) {
//       Cookies.remove('impersonateMode');
//     }
//   }
// });

export default router;
